import { Flex, IconFont, Popover, Switch } from '@byecode/ui'
import type { BackgroundNoImageProtocol } from '@lighthouse/core'
import { BACKGROUND_TYPE, CUSTOM_VIEW_LAYOUT } from '@lighthouse/core'
import { FilledInput, FillPickerPopoverDropdown, Group4ByecodeUi, ListItem4ByecodeUi, PopoverDropdownWithClose } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { InheritLabel } from '@/components/InheritLabel'
import { FillColor } from '@/components/StyleSetting/components/FillColor'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'

export const ViewStyles: React.FC<{ customViewDsId: string | undefined }> = ({ customViewDsId }) => {
    const { control, watch } = useFormContext()
    const { handleSetBreakKey } = useSetExtendsKeys()

    const [showGrid, layout] = watch(['breakPoint.viewStyles.showGrid', 'breakPoint.viewLayout.layout'], [true, undefined])
    const isWeekCalendar = layout === CUSTOM_VIEW_LAYOUT.weekCalendar
    const isMonthCalendar = layout === CUSTOM_VIEW_LAYOUT.monthCalendar

    const variableTypeOption = useGetColorVariableOptions({
        customViewDsId
    })

    return (
        <Group4ByecodeUi label="视图样式">
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="日期行" name="breakPoint.viewStyles.calendarDateRow" />

                <Popover width={342} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                    <Popover.Target>
                        <Flex styles={{ root: { width: 180, cursor: 'pointer' } }} justifyContent="flex-end">
                            <IconFont type="ArrowRightSmall" />
                        </Flex>
                    </Popover.Target>
                    <PopoverDropdownWithClose title="日期行">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="星期数文本" name="breakPoint.viewStyles.calendarDateRow.weekColor" />
                            <Controller
                                name="breakPoint.viewStyles.calendarDateRow.weekColor"
                                control={control}
                                render={({ field }) => (
                                    <FillColor
                                        offsetOptions={150}
                                        value={field.value}
                                        onChange={val => {
                                            field.onChange(val)
                                            handleSetBreakKey('breakPoint.viewStyles.calendarDateRow.weekColor')
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="日期数文本" name="breakPoint.viewStyles.calendarDateRow.dateColor" />
                            <Controller
                                name="breakPoint.viewStyles.calendarDateRow.dateColor"
                                control={control}
                                render={({ field }) => (
                                    <FillColor
                                        offsetOptions={150}
                                        value={field.value}
                                        onChange={val => {
                                            field.onChange(val)
                                            handleSetBreakKey('breakPoint.viewStyles.calendarDateRow.dateColor')
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="背景色" name="breakPoint.viewStyles.calendarDateRow.background" />
                            <Controller
                                name="breakPoint.viewStyles.calendarDateRow.background"
                                control={control}
                                render={({ field }) => (
                                    <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={150}>
                                        <Popover.Target>
                                            <FilledInput
                                                style={{ width: 180 }}
                                                value={field.value}
                                                onClear={() => {
                                                    field.onChange({ type: BACKGROUND_TYPE.color })

                                                    handleSetBreakKey('breakPoint.viewStyles.calendarDateRow.background')
                                                }}
                                                variableTypeOption={variableTypeOption}
                                            />
                                        </Popover.Target>
                                        <FillPickerPopoverDropdown
                                            title="填充"
                                            value={field.value}
                                            onChange={val => {
                                                if (isDeepEqual(val, field.value)) {
                                                    return
                                                }
                                                field.onChange(val)
                                                handleSetBreakKey('breakPoint.viewStyles.calendarDateRow.background')
                                            }}
                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.variable]}
                                            variableTypeOption={variableTypeOption}
                                        />
                                    </Popover>
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </PopoverDropdownWithClose>
                </Popover>
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="时间列" name="breakPoint.viewStyles.calendarTimeCol" />
                <Popover width={342} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                    <Popover.Target>
                        <Flex styles={{ root: { width: 180, cursor: 'pointer' } }} justifyContent="flex-end">
                            <IconFont type="ArrowRightSmall" />
                        </Flex>
                    </Popover.Target>
                    <PopoverDropdownWithClose title="时间列">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="时间文本" name="breakPoint.viewStyles.calendarTimeCol.timeColor" />
                            <Controller
                                name="breakPoint.viewStyles.calendarTimeCol.timeColor"
                                control={control}
                                render={({ field }) => (
                                    <FillColor
                                        offsetOptions={150}
                                        value={field.value}
                                        onChange={val => {
                                            field.onChange(val)
                                            handleSetBreakKey('breakPoint.viewStyles.calendarTimeCol.timeColor')
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="背景色" name="breakPoint.viewStyles.calendarTimeCol.background" />
                            <Controller
                                name="breakPoint.viewStyles.calendarTimeCol.background"
                                control={control}
                                render={({ field }) => (
                                    <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={150}>
                                        <Popover.Target>
                                            <FilledInput
                                                style={{ width: 180 }}
                                                value={field.value}
                                                onClear={() => {
                                                    field.onChange({ type: BACKGROUND_TYPE.color })

                                                    handleSetBreakKey('breakPoint.viewStyles.calendarTimeCol.background')
                                                }}
                                                variableTypeOption={variableTypeOption}
                                            />
                                        </Popover.Target>
                                        <FillPickerPopoverDropdown
                                            title="填充"
                                            value={field.value}
                                            onChange={val => {
                                                if (isDeepEqual(val, field.value)) {
                                                    return
                                                }
                                                field.onChange(val)
                                                handleSetBreakKey('breakPoint.viewStyles.calendarTimeCol.background')
                                            }}
                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.variable]}
                                            variableTypeOption={variableTypeOption}
                                        />
                                    </Popover>
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </PopoverDropdownWithClose>
                </Popover>
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="显示网格" name="breakPoint.viewStyles.showGrid" />
                <Controller
                    control={control}
                    defaultValue={!0}
                    name="breakPoint.viewStyles.showGrid"
                    render={({ field }) => (
                        <Switch
                            checked={field.value}
                            onChange={e => {
                                field.onChange(e.target.checked)
                                handleSetBreakKey('breakPoint.viewStyles.showGrid')
                            }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            {showGrid && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="网格颜色" name="breakPoint.viewStyles.gridColor" />
                    <Controller
                        control={control}
                        name="breakPoint.viewStyles.gridColor"
                        render={({ field }) => (
                            <FillColor
                                enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.viewStyles.gridColor')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {isWeekCalendar && (
                <>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="隐藏时间段拆分" name="breakPoint.viewStyles.hideTimeSplit" />
                        <Controller
                            control={control}
                            name="breakPoint.viewStyles.hideTimeSplit"
                            render={({ field }) => (
                                <Switch
                                    checked={field.value}
                                    onChange={e => {
                                        field.onChange(e.target.checked)
                                        handleSetBreakKey('breakPoint.viewStyles.hideTimeSplit')
                                    }}
                                />
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="显示周切换" name="breakPoint.viewStyles.showWeekSwitcher" />
                        <Controller
                            control={control}
                            name="breakPoint.viewStyles.showWeekSwitcher"
                            render={({ field }) => (
                                <Switch
                                    checked={field.value ?? false}
                                    onChange={e => {
                                        field.onChange(e.target.checked)
                                        handleSetBreakKey('breakPoint.viewStyles.showWeekSwitcher')
                                    }}
                                />
                            )}
                        />
                    </ListItem4ByecodeUi>
                </>
            )}

            {(isWeekCalendar || isMonthCalendar) && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="跟随最高行的高度" name="breakPoint.viewStyles.uniformRowHeight" />
                    <Controller
                        control={control}
                        name="breakPoint.viewStyles.uniformRowHeight"
                        render={({ field }) => (
                            <Switch
                                checked={field.value ?? false}
                                onChange={e => {
                                    field.onChange(e.target.checked)
                                    handleSetBreakKey('breakPoint.viewStyles.uniformRowHeight')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}
        </Group4ByecodeUi>
    )
}
